import React, { useEffect, useState } from 'react';
import {
    Card,
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    Row,
    Col,
    message,
    Typography,
    Space,
    Divider,
    Switch,
    Spin,
    Table,
    Modal,
    Tag,
    Popconfirm
} from 'antd';
import {
    SaveOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons';
import { ArrowLeft, Briefcase, Brain } from 'lucide-react';
import { getAssistantPlan, updateAssistantPlan, createAssistantPlan } from "../../../services/assistant-plan";
import { getAvailableModels, createAvailableModel, deleteAvailableModel } from "../../../services/available-models";
import AdminLayout from "../../../components/admin-layout";
import { useNavigate, useParams, Link } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

const PROVIDERS = [
    { label: 'OpenAI', value: 'OPENAI' },
    { label: 'Deepseek', value: 'DEEPSEEK' },
    { label: 'Google', value: 'GOOGLE' },
    { label: 'Groq', value: 'GROQ' },
    { label: 'Nvidia', value: 'NVIDIA' },
    { label: 'Local', value: 'LOCAL' },
    { label: 'Fireworks', value: 'FIREWORKS' },
    { label: 'OpenRouter', value: 'OPENROUTER' },
    { label: 'Verboo', value: 'VERBOO' },
    { label: 'MIMO', value: 'MIMO' },
];

const llmOptions = {
    "OPENAI": [
        { label: "GPT 4o", value: "gpt-4o" },
        { label: "GPT 4o Mini", value: "gpt-4o-mini" },
        { label: "GPT 4.1 Mini", value: "gpt-4.1-mini" },
        { label: "GPT 4.1", value: "gpt-4.1" },
        { label: "GPT 4.1 Nano", value: "gpt-4.1-nano" },
        { label: "o3 Mini", value: "o3-mini" },
        { label: "GPT 5", value: "gpt-5" },
        { label: "GPT 5 Mini", value: "gpt-5-mini" },
        { label: "GPT 5 Nano", value: "gpt-5-nano" },
        { label: "GPT 5.1", value: "gpt-5.1" },
        { label: "GPT 5.2", value: "gpt-5.2" },
        { label: "GPT 5.1 Mini", value: "gpt-5.1-mini" },
        { label: "GPT 5.1 Nano", value: "gpt-5.1-nano" }
    ],
    "GROQ": [
        { label: "LLaMA 3.1 8b Instant", value: "llama-3.1-8b-instant" },
        { label: "LLaMA 3 8b", value: "llama3-8b-8192" },
        { label: "OpenAI GPT OSS 20b", value: "openai/gpt-oss-20b" },
        { label: "OpenAI GPT OSS 120b", value: "openai/gpt-oss-120b" },
    ],
    "DEEPSEEK": [
        { label: "Deepseek Chat", value: "deepseek-chat" },
        { label: "Deepseek Reasoner", value: "deepseek-reasoner" },
    ],
    "GOOGLE": [
        { label: "Gemini 1.5 Flash v1", value: "gemini-1.5-flash-001" },
        { label: "Gemini 2.0 Flash Experimental", value: "gemini-2.0-flash-exp" },
        { label: "Gemini 2.0 Flash v1", value: "gemini-2.0-flash-001" },
        { label: "Gemini 2.0 Flash Lite v1", value: "gemini-2.0-flash-lite-001" },
        { label: "Gemini 2.0 Flash Lite (Preview 05/02/25)", value: "gemini-2.0-flash-lite-preview-02-05" },
        { label: "Gemini 2.5 Pro Exp (Preview 25/03/25)", value: "gemini-2.5-pro-preview-03-25" },
        { label: "Gemini 2.0 Flash Thinking (Preview 21/01/25)", value: "gemini-2.0-flash-thinking-exp-01-21" },
        { label: "Gemini 2.5 Flash Lite", value: "gemini-2.5-flash-lite" },
        { label: "Gemini 2.5 Flash (Preview 20/05/25)", value: "gemini-2.5-flash-preview-05-20" },
        { label: "Gemini 2.5 Flash", value: "gemini-2.5-flash" },
        { label: "Gemini 2.5 Pro", value: "gemini-2.5-pro" },
        { label: "Gemini 3 Pro Preview", value: "gemini-3-pro-preview" },
        { label: "Gemini 2.5 Flash Lite (Preview 17/06/2025)", value: "gemini-2.5-flash-lite-preview-06-17" },
        { label: "Gemini 1.5 Pro v1", value: "gemini-1.5-pro-001" }
    ],
    "FIREWORKS": [
        { label: "Qwen 3 235B A22B", value: "accounts/fireworks/models/qwen3-235b-a22b" },
        { label: "Qwen 3 30B A3B", value: "accounts/fireworks/models/qwen3-30b-a3b" },
        { label: "LLaMA 4 Maverick Instruct", value: "accounts/fireworks/models/llama4-maverick-instruct-basic" },
        { label: "LLaMA 4 Scout Instruct", value: "accounts/fireworks/models/llama4-scout-instruct-basic" },
        { label: "Kimi K2 Instruct", value: "accounts/fireworks/models/kimi-k2-instruct" },
        { label: "Qwen 3 235B A22B Instruct (25/07)", value: "accounts/fireworks/models/qwen3-235b-a22b-instruct-2507" },
        { label: "OpenAI GPT OSS 20b", value: "accounts/fireworks/models/gpt-oss-20b" },
        { label: "OpenAI GPT OSS 120b", value: "accounts/fireworks/models/gpt-oss-120b" },
        { label: "Deepseek V3p1 Terminus", value: "deepseek-ai/deepseek-v3p1-terminus" },
        { label: "GLM 4.6", value: "fireworks/glm-4p6" },
        { label: "Minimax M2", value: "fireworks/minimax-m2" },
        { label: "Qwen Vision 235B A22B Instruct", value: "accounts/fireworks/models/qwen3-vl-235b-a22b-instruct" },
        { label: "Qwen Vision 30B A3B Instruct", value: "accounts/fireworks/models/qwen3-vl-30b-a3b-instruct" }
    ],
    "OPENROUTER": [
        { label: "Qwen 3.5 27B", value: "qwen/qwen3.5-27b" },
        { label: "Gemma 4 31B IT", value: "google/gemma-4-31b-it" }
    ],
    "VERBOO": [
        { label: "Qwen3.6 35B A3B", value: "qwen/qwen3.6-35b-a3b" }
    ],
    "MIMO": [
        { label: "Mimo v2.5", value: "mimo-v2.5" },
        { label: "Mimo v2.5 Pro", value: "mimo-v2.5-pro" },
        { label: "Mimo v2.5 Pro UltraSpeed", value: "mimo-v2.5-pro-ultraspeed" },
    ]
};

export default function AssistantPlansEdit() {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;

    // Available Models State
    const [models, setModels] = useState([]);
    const [modelsLoading, setModelsLoading] = useState(false);
    const [addModelModalVisible, setAddModelModalVisible] = useState(false);
    const [addModelForm] = Form.useForm();
    const [addingModel, setAddingModel] = useState(false);

    // Watch for provider changes
    const selectedProvider = Form.useWatch('provider', addModelForm);
    const [currentModelOptions, setCurrentModelOptions] = useState([]);

    useEffect(() => {
        if (selectedProvider && llmOptions[selectedProvider]) {
            setCurrentModelOptions(llmOptions[selectedProvider]);
        } else {
            setCurrentModelOptions([]);
        }
    }, [selectedProvider]);

    useEffect(() => {
        if (isEditing) {
            loadPlan(id);
        } else {
            form.setFieldsValue({
                type: 'pay_per_use',
                isActive: true,
                maxRequests: -1,
                maxMessages: -1,
                maxSessions: -1
            })
        }
    }, [id]);

    const loadPlan = async (planId) => {
        setLoading(true);
        try {
            const response = await getAssistantPlan(planId);
            const planData = response.data || response;
            form.setFieldsValue(planData);

            // Load models if present or fetch them
            if (planData.availableModels) {
                setModels(planData.availableModels);
            } else {
                fetchModels(planId);
            }
        } catch (error) {
            console.error(error);
            message.error("Falha ao carregar detalhes do plano");
            navigate('/plans/assistant');
        } finally {
            setLoading(false);
        }
    };

    const fetchModels = async (planId) => {
        setModelsLoading(true);
        try {
            const response = await getAvailableModels({ assistantPlanId: planId });
            setModels(response.data || response);
        } catch (error) {
            console.error(error);
            message.warning("Não foi possível carregar os modelos vinculados.");
        } finally {
            setModelsLoading(false);
        }
    };

    const handleFinish = async (values) => {
        setSubmitting(true);
        try {
            if (isEditing) {
                await updateAssistantPlan(id, values);
                message.success("Plano atualizado com sucesso!");
            } else {
                await createAssistantPlan(values);
                message.success("Plano criado com sucesso!");
                navigate('../');
            }
        } catch (error) {
            console.error(error);
            message.error("Falha ao salvar plano");
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddModel = async (values) => {
        setAddingModel(true);
        try {
            // Ensure model is a string (take the last tag if array)
            const modelName = Array.isArray(values.model)
                ? values.model[values.model.length - 1]
                : values.model;

            if (!modelName) {
                message.error("Por favor, informe o nome do modelo.");
                setAddingModel(false);
                return;
            }

            await createAvailableModel({
                ...values,
                model: modelName,
                assistantPlanId: parseInt(id)
            });

            message.success("Modelo adicionado com sucesso!");
            setAddModelModalVisible(false);
            addModelForm.resetFields();
            fetchModels(id); // Refresh list
        } catch (error) {
            console.error(error);
            message.error("Falha ao adicionar modelo");
        } finally {
            setAddingModel(false);
        }
    };

    const handleDeleteModel = async (modelId) => {
        try {
            await deleteAvailableModel(modelId);
            message.success("Modelo removido com sucesso!");
            fetchModels(id); // Refresh list
        } catch (error) {
            console.error(error);
            message.error("Falha ao remover modelo");
        }
    }

    const modelColumns = [
        {
            title: 'Provedor',
            dataIndex: 'provider',
            key: 'provider',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'Modelo',
            dataIndex: 'model',
            key: 'model',
            render: (text) => <Text strong>{text}</Text>
        },
        {
            title: 'Padrão',
            dataIndex: 'isDefault',
            key: 'isDefault',
            render: (isDefault) => isDefault ? <Tag color="green">Sim</Tag> : <Text type="secondary">Não</Text>
        },
        {
            title: '',
            key: 'actions',
            render: (_, record) => (
                <Popconfirm
                    title="Remover modelo"
                    description="Tem certeza que deseja desvincular este modelo?"
                    onConfirm={() => handleDeleteModel(record.id)}
                    okText="Sim"
                    cancelText="Não"
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    if (loading) {
        return (
            <AdminLayout withBackground={false}>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }}>
                    <Spin size="large" />
                </div>
            </AdminLayout>
        )
    }

    return (
        <AdminLayout withBackground={false}>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{ isActive: true }}
            >
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        {/* Header Actions */}
                        <Row justify="space-between" align="middle">
                            <Col>
                                <Space align="center" style={{ marginRight: 6, marginBottom: 16, marginTop: 16 }}>
                                    <Link to="../">
                                        <Button icon={<ArrowLeft size={16} />} type="text" />
                                    </Link>
                                    <Briefcase size={14} style={{ marginTop: "4px" }} />
                                    <Typography.Title style={{ padding: 0, margin: 0 }} level={5} strong>
                                        {isEditing ? 'Editar Plano' : 'Novo Plano'}
                                    </Typography.Title>
                                </Space>
                            </Col>
                            <Col>
                                <Space>
                                    <Button onClick={() => navigate('../')}>Cancelar</Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        icon={<SaveOutlined />}
                                        loading={submitting}
                                    >
                                        Salvar
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Col>

                    <Col xs={24} lg={16}>
                        <Card size="small" title="Informações Gerais">
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="name"
                                        label="Nome do Plano"
                                        rules={[{ required: true, message: 'Obrigatório' }]}
                                    >
                                        <Input size="large" placeholder="Ex: Enterprise Gold" />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item
                                        name="description"
                                        label="Descrição Interna"
                                    >
                                        <Input.TextArea rows={3} placeholder="Detalhes sobre o plano..." />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="type" label="Modelo de Cobrança" rules={[{ required: true }]}>
                                        <Select size="large">
                                            <Option value="pack">Pacote Fechado (Mensalidade Fixa)</Option>
                                            <Option value="pay_per_use">Por Uso (Pay-as-you-go)</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12} style={{ display: 'flex', alignItems: 'center' }}>
                                    <Form.Item name="isActive" valuePropName="checked" label="Status" style={{ marginBottom: 0 }}>
                                        <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {isEditing && (
                            <Card
                                size="small"
                                title={<Space><Brain size={16} />Modelos de IA Disponíveis</Space>}
                                style={{ marginTop: 16 }}
                                extra={
                                    <Button
                                        type="primary"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        onClick={() => setAddModelModalVisible(true)}
                                    >
                                        Adicionar Modelo
                                    </Button>
                                }
                            >
                                <Table
                                    dataSource={models}
                                    columns={modelColumns}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    loading={modelsLoading}
                                    locale={{ emptyText: 'Nenhum modelo vinculado' }}
                                />
                            </Card>
                        )}

                        <Card size="small" title="Limites Técnicos" style={{ marginTop: 16 }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="maxRequests" label="Requisições Mensais" tooltip="-1 para ilimitado">
                                        <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item name="maxAssistants" label="Máximo de Assistentes">
                                        <InputNumber style={{ width: '100%' }} min={1} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="maxMessages" label="Máx. Mensagens">
                                        <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="maxSessions" label="Máx. Sessões">
                                        <InputNumber style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item name="maxLogSize" label="Retenção de Logs" tooltip="Quantidade de logs mantidos">
                                        <InputNumber style={{ width: '100%' }} min={0} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    <Col xs={24} lg={8}>
                        <Card size="small" title="Precificação" extra={<Text type="secondary" style={{ fontSize: 12 }}>Valores em BRL</Text>}>
                            <Space direction="vertical" style={{ width: '100%' }} size="large">
                                <Form.Item name="price" label="Mensalidade Base" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        size="large"
                                        formatter={value => `R$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                        parser={value => value.replace(/R\$\s?|(,*)/g, '')}
                                    />
                                </Form.Item>

                                <Divider style={{ margin: '12px 0' }} orientation="left"><Text type="secondary" style={{ fontSize: 12 }}>Custos Variáveis</Text></Divider>

                                <Form.Item name="pricePerMessage" label="Custo por Mensagem" style={{ marginBottom: 12 }}>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        step={0.0001}
                                        addonBefore="R$"
                                    />
                                </Form.Item>
                                <Form.Item name="pricePerSession" label="Custo por Sessão" style={{ marginBottom: 12 }}>
                                    <InputNumber
                                        style={{ width: '100%' }}
                                        min={0}
                                        step={0.0001}
                                        addonBefore="R$"
                                    />
                                </Form.Item>
                                <Form.Item name="minRequestsToBeBillable" label="Franquia (Min. Requests)" tooltip="Cobrança variável inicia após X requisições">
                                    <InputNumber style={{ width: '100%' }} min={0} />
                                </Form.Item>
                            </Space>
                        </Card>

                        <Card size="small" title="Armazenamento" style={{ marginTop: 16 }}>
                            <Form.Item name="maxStorage" label="Limite em Bytes">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                />
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>
            </Form>

            <Modal
                title="Adicionar Modelo de IA"
                open={addModelModalVisible}
                onCancel={() => setAddModelModalVisible(false)}
                onOk={addModelForm.submit}
                confirmLoading={addingModel}
                destroyOnClose
            >
                <Form
                    form={addModelForm}
                    layout="vertical"
                    onFinish={handleAddModel}
                    initialValues={{ isDefault: false, provider: 'OPENAI' }}
                >
                    <Form.Item name="provider" label="Provedor" rules={[{ required: true }]}>
                        <Select options={PROVIDERS} />
                    </Form.Item>
                    <Form.Item
                        name="model"
                        label="Modelo (Nome Técnico)"
                        rules={[{ required: true, message: 'Selecione ou digite um modelo' }]}
                        help="Ex: gpt-4o, llama-3.1-70b"
                    >
                        <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            placeholder="Selecione ou digite o nome do modelo"
                            options={currentModelOptions}
                            tokenSeparators={[',']}
                            maxTagCount={1}
                            onChange={(vals) => {
                                // Enforce single selection behavior: use only the latest selected/typed value
                                if (Array.isArray(vals) && vals.length > 1) {
                                    const latest = vals[vals.length - 1];
                                    // Use setTimeout to avoid render loop warning inside onChange
                                    setTimeout(() => {
                                        addModelForm.setFieldValue('model', [latest]);
                                    }, 0);
                                }
                            }}
                        />
                    </Form.Item>
                    <Form.Item name="isDefault" valuePropName="checked" label="Modelo Padrão?">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </AdminLayout>
    );
}
